/** The portal's icons: 20px grid, 1.6 stroke, `currentColor`. */
type IconProps = { className?: string }

const svg = (path: JSX.Element) =>
  function Icon({ className = 'h-[18px] w-[18px]' }: IconProps) {
    return (
      <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {path}
      </svg>
    )
  }

export const OverviewIcon = svg(<><rect x="3" y="3" width="6" height="7" rx="1.5" /><rect x="11" y="3" width="6" height="4" rx="1.5" /><rect x="11" y="10" width="6" height="7" rx="1.5" /><rect x="3" y="13" width="6" height="4" rx="1.5" /></>)
export const LiveIcon = svg(<><circle cx="10" cy="10" r="2.2" /><path d="M5.6 5.6a6.2 6.2 0 0 0 0 8.8M14.4 5.6a6.2 6.2 0 0 1 0 8.8M3.3 3.3a9.5 9.5 0 0 0 0 13.4M16.7 3.3a9.5 9.5 0 0 1 0 13.4" /></>)
export const UsersIcon = svg(<><circle cx="7.5" cy="7" r="3" /><path d="M2.5 16.5c.4-2.8 2.4-4.5 5-4.5s4.6 1.7 5 4.5" /><circle cx="14.5" cy="7.5" r="2.3" /><path d="M14 12c2 .1 3.3 1.5 3.6 3.5" /></>)
export const PartnersIcon = svg(<><path d="M2.5 9.5 6 6l3 1.5L12 6l5.5 3.5" /><path d="m6 6-3.5 3.5 4.5 4.5c.6.6 1.5.6 2.1 0l.4-.4M12 6l5.5 3.5-4.2 4.2c-.6.6-1.5.6-2.1 0L8.5 11" /><path d="m10.2 15.1 1.1 1.1" /></>)
export const SearchIcon = svg(<><circle cx="9" cy="9" r="5.2" /><path d="m13 13 3.5 3.5" /></>)
export const BellIcon = svg(<><path d="M5 8.5a5 5 0 0 1 10 0c0 3.5 1.5 4.5 1.5 5.5h-13c0-1 1.5-2 1.5-5.5Z" /><path d="M8.3 16.5a1.8 1.8 0 0 0 3.4 0" /></>)
export const BellOffIcon = svg(<><path d="M6 5.5A5 5 0 0 1 15 8.5c0 1.6.3 2.7.7 3.5M13.5 14h-10c0-1 1.5-2 1.5-5.5" /><path d="M8.3 16.5a1.8 1.8 0 0 0 3.4 0M3 3l14 14" /></>)
export const ChevronIcon = svg(<path d="m7.5 4.5 5.5 5.5-5.5 5.5" />)
export const BackIcon = svg(<path d="M12.5 4.5 7 10l5.5 5.5" />)
export const ExternalIcon = svg(<><path d="M11 4h5v5M16 4l-7 7" /><path d="M14 11.5V15a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 4 15V8a1.5 1.5 0 0 1 1.5-1.5H9" /></>)
export const CheckIcon = svg(<path d="m4.5 10.5 3.5 3.5 7.5-8" />)
export const RetryIcon = svg(<><path d="M16 10a6 6 0 1 1-2-4.5" /><path d="M16 3.5v3.5h-3.5" /></>)
export const ShieldIcon = svg(<><path d="M10 2.5 4 5v4.5c0 3.7 2.5 6.7 6 8 3.5-1.3 6-4.3 6-8V5l-6-2.5Z" /><path d="m7.5 10 1.8 1.8L12.7 8.2" /></>)
export const SunIcon = svg(<><circle cx="10" cy="10" r="3.3" /><path d="M10 2.5v1.8M10 15.7v1.8M2.5 10h1.8M15.7 10h1.8M4.7 4.7 6 6M14 14l1.3 1.3M4.7 15.3 6 14M14 6l1.3-1.3" /></>)
export const MoonIcon = svg(<path d="M16.5 11.8A6.8 6.8 0 0 1 8.2 3.5a6.8 6.8 0 1 0 8.3 8.3Z" />)
export const AccountIcon = svg(<><circle cx="10" cy="7.5" r="3.2" /><path d="M3.8 16.8c.7-3 3.1-4.8 6.2-4.8s5.5 1.8 6.2 4.8" /></>)

export function LogoMark({ className = 'h-8 w-8' }: IconProps) {
  return (
    <span className={`relative grid shrink-0 place-items-center rounded-[9px] bg-ink text-oncolor ${className}`}>
      <svg className="h-4 w-4" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M3.25 3.5 6.75 7l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
        <path d="M7.25 3.5 10.75 7l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* The amber dot is what tells this door from the job seeker app's. */}
      <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-surface bg-warning" aria-hidden="true" />
    </span>
  )
}

export function Avatar({ name, size = 'md' }: { name: string | null | undefined; size?: 'sm' | 'md' | 'lg' }) {
  const letters = (name ?? '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
  const box = { sm: 'h-7 w-7 text-[11px]', md: 'h-9 w-9 text-[12.5px]', lg: 'h-12 w-12 text-[15px]' }[size]
  return <span className={`grid shrink-0 place-items-center rounded-full bg-accent-soft font-semibold text-ink-soft ${box}`}>{letters || '?'}</span>
}
